/**
 * BankAccountList - Displays list of bank accounts with management actions
 */

import type { BankAccountPublic } from '@garden-platform/shared';
import type * as React from 'react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { useBankAccounts, useDeleteBankAccount, useSetDefaultBankAccount } from '../../hooks';
import { BankAccountCard } from './BankAccountCard';
import { BankAccountFormDialog } from './BankAccountFormDialog';

export interface BankAccountListProps {
  className?: string;
}

export const BankAccountList: React.FC<BankAccountListProps> = ({ className }) => {
  const [showInactive, setShowInactive] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<BankAccountPublic | undefined>();

  const { data: bankAccounts, isLoading } = useBankAccounts(showInactive);
  const deleteMutation = useDeleteBankAccount();
  const setDefaultMutation = useSetDefaultBankAccount();

  const handleEdit = (account: BankAccountPublic) => {
    setEditingAccount(account);
    setIsDialogOpen(true);
  };

  const handleAdd = () => {
    setEditingAccount(undefined);
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this bank account?')) {
      await deleteMutation.mutateAsync(id);
    }
  };

  const handleSetDefault = async (id: string) => {
    await setDefaultMutation.mutateAsync(id);
  };

  const handleDialogClose = (open: boolean) => {
    setIsDialogOpen(open);
    if (!open) {
      setEditingAccount(undefined);
    }
  };

  if (isLoading) {
    return (
      <Card className={className}>
        <CardContent className="flex items-center justify-center py-12">
          <p className="text-muted-foreground">Loading bank accounts...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className={className}>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle>Bank Accounts</CardTitle>
              <CardDescription>Manage bank accounts for cheque printing</CardDescription>
            </div>
            <Button onClick={handleAdd}>Add Account</Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="showInactive"
              checked={showInactive}
              onCheckedChange={(checked) => setShowInactive(checked === true)}
            />
            <Label htmlFor="showInactive" className="text-sm font-normal">
              Show inactive accounts
            </Label>
          </div>

          {!bankAccounts || bankAccounts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <p className="text-muted-foreground mb-4">No bank accounts found</p>
              <Button variant="outline" onClick={handleAdd}>
                Add your first bank account
              </Button>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {bankAccounts.map((account) => (
                <BankAccountCard
                  key={account.id}
                  bankAccount={account}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onSetDefault={handleSetDefault}
                  isDeleting={deleteMutation.isPending}
                  isSettingDefault={setDefaultMutation.isPending}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <BankAccountFormDialog
        open={isDialogOpen}
        onOpenChange={handleDialogClose}
        bankAccount={editingAccount}
      />
    </>
  );
};

BankAccountList.displayName = 'BankAccountList';
